import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useMemo } from 'react';
import { Menu } from '@shared/components/Menu';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { Pill } from '@shared/components/pill';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { FieldRecord } from '../../../types';
import { LookupOverflowMenuContent } from './LookupOverflowMenuContent';
import { toEntityAndField } from './toEntityAndField';
import { useRelatedCardLookupFields } from './useRelatedCardLookupFields';
import styles from './RelatedLookupFieldOverflowMenu.module.css';

interface LazyRelatedLookupOverflowMenuProps {
  component: API.RelatedCardLookupComponent;
  relatedField: RelatedEntityField | null;
  extraCount: number;
  workflowTemplateId: string;
  title?: string;
  pillTextStyle?: CSSProperties;
}

/**
 * Lookup overflow menu for the collection view. Unlike the card-form menu (which receives
 * already-resolved values), the looked-up values are resolved lazily: `Menu.Content` unmounts
 * its children while closed, so the resolving hook — which may fetch nested related cards —
 * only runs once the user actually opens the menu.
 */
export function LazyRelatedLookupOverflowMenu({
  component,
  relatedField,
  extraCount,
  workflowTemplateId,
  title,
  pillTextStyle,
}: LazyRelatedLookupOverflowMenuProps) {
  return (
    <Menu>
      <Menu.Trigger>
        <View>
          <Pill sections={[`+${extraCount}`]} textStyle={pillTextStyle} />
        </View>
      </Menu.Trigger>
      <Menu.Content className={styles.lookupOverflowMenu}>
        <LazyLookupOverflowContent
          component={component}
          relatedField={relatedField}
          workflowTemplateId={workflowTemplateId}
          title={title}
        />
      </Menu.Content>
    </Menu>
  );
}

function LazyLookupOverflowContent({
  component,
  relatedField,
  workflowTemplateId,
  title,
}: Omit<LazyRelatedLookupOverflowMenuProps, 'extraCount'>) {
  // The hook only reads the level-1 related field from this record (by relatedCardComponentId).
  const fields: FieldRecord = useMemo(
    () => (isNil(relatedField) ? {} : { [component.relatedCardComponentId]: relatedField }),
    [component.relatedCardComponentId, relatedField],
  );
  const { leafComponent, leafEntities } = useRelatedCardLookupFields({
    component,
    fields,
    workflowTemplateId,
  });

  if (isEmpty(leafEntities) || isNil(leafComponent)) {
    return (
      <View padding={2} center>
        <LoadingIndicator />
      </View>
    );
  }

  const lookupFields = leafEntities
    .map(leafEntity => toEntityAndField(leafEntity, leafComponent))
    .filter(notNil);

  return (
    <LookupOverflowMenuContent
      lookupFields={lookupFields}
      lookupComponent={leafComponent}
      workflowTemplateId={workflowTemplateId}
      title={title}
    />
  );
}
